import React from "react";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Button } from "../../../../shared/components/ui/Button";
import { Card } from "../../../../shared/components/ui/Card";
import type { UseRegisterNewFormProps } from "./RegisterNewForm.types";
import { useRegisterNewForm } from "./useRegisterNewForm";
import { MaintenanceNewFormFields } from "../MaintenanceNewFormFields/MaintenanceNewFormFields";
import { PoolRegulationNewFormFields } from "../PoolRegulationNewFormFields/PoolRegulationNewFormFields";
import { AdverseEventNewFormFields } from "../AdverseEventNewFormFields/AdverseEventNewFormFields";
import { SwimmerInfoCard } from "../../../swimmers/components/SwimmerInfoCard/SwimmerInfoCard";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import type { AdverseEventType } from "../../../../api/client/types/catalog.types";

interface RegisterNewFormProps extends UseRegisterNewFormProps {
  isSubmitting?: boolean;
  submitLabel?: string;
  swimmer?: Swimmer;
  adverseEventTypes?: AdverseEventType[];
  /**
   * Controls whether the event date can be edited.
   * - Lifeguard create flow: false (only time is editable, date is today)
   * - Admin edit flow: true (date and time are editable)
   */
  allowDateEdit?: boolean;
}

/**
 * TypeSpecificFieldsSection Component
 * Provides consistent styling and spacing for report type-specific fields.
 * These fields appear immediately after the report type selection.
 */
interface TypeSpecificFieldsSectionProps {
  title?: string;
  children: React.ReactNode;
}

const TypeSpecificFieldsSection: React.FC<TypeSpecificFieldsSectionProps> = ({
  title,
  children,
}) => {
  return (
    <div className={`border-t border-gray-200 ${title ? "mt-6 pt-6" : "mt-4 pt-2"}`}>
      {title && <h3 className="text-md font-medium text-gray-700 mb-4">{title}</h3>}
      <div>{children}</div>
    </div>
  );
};

export const RegisterNewForm: React.FC<RegisterNewFormProps> = ({
  onSubmit,
  reportTypes,
  maintenanceTypes = [],
  poolRegulations = [],
  interventionLevels = [],
  adverseEventTypes = [],
  initialData,
  isSubmitting = false,
  submitLabel = "Registrar",
  swimmer,
  allowDateEdit = false,
}) => {
  const {
    register,
    setValue,
    handleSubmit,
    control,
    errors,
    isValid,
    currentTimeValue,
    currentDateDisplay,
    currentDateValue,
    handleTimeChange,
    handleDateChange,
    reportTypeOptions,
    isMaintenanceSelected,
    isEmergencyElementsSelected,
    isPoolRegulationSelected,
    isFirstAidStationSelected,
    isAdverseEventSelected,
    isLlamadoDeAtencionSelected,
    isAccidentSelected,
    isIncidentSelected,
    isAccidentOrIncidentSelected,
    isMeteorologicalConditionsSelected,
    selectedAdverseEventTypeId,
    eventDescriptionPlaceholder,
    eventDescriptionRows,
    showCommonFields,
  } = useRegisterNewForm({
    onSubmit,
    reportTypes,
    maintenanceTypes,
    poolRegulations,
    interventionLevels,
    adverseEventTypes,
    initialData,
    swimmer,
  });

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 w-full max-w-4xl mx-auto"
    >
      {/* Swimmer Information Section */}
      {swimmer && <SwimmerInfoCard swimmer={swimmer} />}

      {/* Event Information Section */}
      <Card variant="formSection">
        <h2 className="text-lg font-medium text-gray-800 mb-4">
          Información del Evento
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Event Timestamp - Date from event (edit) or today (create), time selectable */}
          <div className="md:col-span-2">
            <FormField
              type="dateTime"
              label="Fecha y Hora del Evento"
              required
              dateTimeProps={{
                currentDateDisplay,
                timeValue: currentTimeValue,
                onTimeChange: handleTimeChange,
                minTime: "08:00",
                maxTime: "18:00",
                ...(allowDateEdit
                  ? {
                      dateValue: currentDateValue,
                      onDateChange: handleDateChange,
                    }
                  : {}),
                id: "event_timestamp",
                name: "event_timestamp",
              }}
              error={errors.event_timestamp?.message}
            />
            {/* Hidden input to store the full datetime for form validation */}
            <input type="hidden" {...register("event_timestamp")} />
          </div>

          {/* Report Type */}
          <div className="md:col-span-2">
            <FormField
              type="radioGroup"
              label="Tipo de Reporte"
              required
              radioGroupProps={{
                control,
                name: "report_type_id",
                options: reportTypeOptions,
              }}
              error={errors.report_type_id?.message}
            />
          </div>
        </div>

        {/* Type-Specific Fields - Shown immediately after report type selection */}
        {isMaintenanceSelected && (
          <TypeSpecificFieldsSection title="Información de Mantenimiento">
            <MaintenanceNewFormFields
              register={register as any}
              errors={errors as any}
              maintenanceTypes={maintenanceTypes}
              showCard={false}
              hideTypeSelector={isEmergencyElementsSelected}
            />
          </TypeSpecificFieldsSection>
        )}

        {isPoolRegulationSelected && (
          <TypeSpecificFieldsSection title="Información de Incumplimiento">
            <PoolRegulationNewFormFields
              register={register as any}
              errors={errors as any}
              poolRegulations={poolRegulations}
              interventionLevels={interventionLevels}
              showCard={false}
            />
          </TypeSpecificFieldsSection>
        )}

        {/* Common Fields - Only shown after type-specific section is filled */}
        {showCommonFields && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200">
            {/* Security Officer Name - Hidden for "Puesto de P.A / Botiquín" and "Llamado de Atención 1/2" */}
            {!isFirstAidStationSelected && !isLlamadoDeAtencionSelected && (
              <div className="md:col-span-2">
                <FormField
                  type="input"
                  label="(W) Apellido del Oficial de Seguridad"
                  required
                  inputProps={{
                    ...register("security_officer_name"),
                    type: "text",
                    placeholder: "Apellido",
                    maxLength: 150,
                    className: "text-sm",
                  }}
                  error={errors.security_officer_name?.message}
                />
              </div>
            )}

            {/* Event Description - Full width */}
            <div className="md:col-span-2">
              <FormField
                type="textarea"
                label="Descripción del Evento"
                required
                textareaProps={{
                  ...register("event_description"),
                  id: "event_description",
                  rows: eventDescriptionRows,
                  placeholder: eventDescriptionPlaceholder,
                  className: "text-sm",
                }}
                error={errors.event_description?.message}
              />
            </div>

            {/* Spent Items - Only for "Accidente" */}
            {isAccidentSelected && (
              <div className="md:col-span-2">
                <FormField
                  type="textarea"
                  label="Insumos Gastados"
                  required
                  textareaProps={{
                    ...register("spent_items"),
                    id: "spent_items",
                    rows: 2,
                    placeholder: "Describa los insumos gastados...",
                    className: "text-sm",
                  }}
                  error={errors.spent_items?.message}
                />
              </div>
            )}
          </div>
        )}

        {/* Adverse Event Factors — independent section at the end, only for Accidente / Incidentes */}
        {isAdverseEventSelected &&
          isAccidentOrIncidentSelected &&
          showCommonFields && (
            <TypeSpecificFieldsSection>
              <AdverseEventNewFormFields
                errors={errors as any}
                control={control as any}
                setValue={setValue as any}
                adverseEventTypes={adverseEventTypes}
                swimmer={swimmer}
                showCard={false}
                showTypeSelection={false}
                isAccidentSelected={isAccidentSelected}
                isIncidentSelected={isIncidentSelected}
                isMeteorologicalConditionsSelected={isMeteorologicalConditionsSelected}
                adverseEventsTypeId={
                  selectedAdverseEventTypeId
                    ? parseInt(selectedAdverseEventTypeId, 10)
                    : null
                }
              />
            </TypeSpecificFieldsSection>
          )}
      </Card>

      {/* Submit */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-2">
        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="text-sm px-8 py-3 font-semibold"
        >
          {isSubmitting ? "Registrando..." : submitLabel}
        </Button>
      </div>
    </form>
  );
};
